import { getRandomInt, questionSequence, shuffle } from "../Shared/Helpers.tsx";
import { generateProblemPages } from "../Shared/Generation/problemPages.ts";
import { greatestCommonDivisor } from "./FractionGenerator.ts";
import { ProblemData, RatioQuestionOptions } from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";
import { PrintedLanguage, printedLanguageOf } from "./PrintedLanguage.ts";
import { ratePairs as enPairs, ratioPrompts as en } from "../Files/Prompts/en/ratio.ts";
import { ratePairs as esPairs, ratioPrompts as es } from "../Files/Prompts/es/ratio.ts";
import { ratePairs as frPairs, ratioPrompts as fr } from "../Files/Prompts/fr/ratio.ts";

const PROMPTS: Record<PrintedLanguage, typeof en> = { en, es, fr };
const RATE_PAIR_TABLE: Record<PrintedLanguage, typeof enPairs> = { en: enPairs, es: esPairs, fr: frPairs };

/**
 * Ratio. See ADR 0035.
 *
 * This is the one of ADR 0022 §2's three number systems where its estimate -
 * "a new pair type and its own notation" - turns out to be right. Integers
 * were a format with a horizontal renderer; percent was notation over values
 * that already existed. A ratio is genuinely a relationship between two
 * numbers that has to stay a pair: simplifying `12 : 18` is one operation on
 * the pair, not two on its halves.
 *
 * The pair only exists as a pair in here. `display` carries the answer as the
 * string `2 : 3`, which leaves the answer-key machinery alone.
 *
 * Proportional Tables (ADR 0104) is outside Mixed and is the one question
 * here that draws a table. Its tables are built from the constant (or, for a
 * table that is not proportional, from the slope and a nonzero intercept), so
 * nothing is rejected. Each printed page - and each half of a half page, which is
 * its own call - deals its three questions evenly, with the "is it
 * proportional" tables split yes and no as evenly as the count allows.
 *
 * Proportional Graphs (ADR 0105) is its sibling: the same three questions, the
 * third reading a value past the edge of the grid instead of filling in a table,
 * dealt the same way. Its graphs are built from the answer too (a valid
 * constant and scale, or a slope and intercept that pass the readability rules)
 * and never rejected.
 */

/** The colon is spaced: `12:18` is a time, and this site prints those. */
const COLON = " : ";

function questionTypes(question: RatioQuestionOptions): RatioQuestionOptions[] {
    if (question === RatioQuestionOptions.Mixed) {
        return [
            RatioQuestionOptions.Simplify,
            RatioQuestionOptions.Equivalent,
            RatioQuestionOptions.UnitRate,
            RatioQuestionOptions.Proportion,
        ];
    }
    return [question];
}

function usableMax(maxValue: number): number {
    return Math.max(12, Math.floor(maxValue) || 50);
}

/**
 * A pair in lowest terms, to build the others from.
 *
 * Coprime by construction rather than by rejection: two values are drawn and
 * divided by their own greatest common divisor, so what comes back is always
 * already reduced.
 *
 * Never `1 : 1`. Any pair of equal values reduces to it, and it is the one
 * ratio with no relationship in it - as a proportion it gives `1/1 = x/67`,
 * whose answer is the number already printed.
 */
function reducedPair(limit: number): [number, number] {
    for (let attempt = 0; attempt < 40; attempt++) {
        const first = getRandomInt(1, limit);
        const second = getRandomInt(1, limit);
        const divisor = greatestCommonDivisor(first, second);
        if (first !== second) {
            return [first / divisor, second / divisor];
        }
    }
    return [2, 3];
}

/**
 * How far a reduced pair is scaled up.
 *
 * Capped rather than taken from the whole range: at a setting of 100 a pair of
 * `2 : 3` could otherwise be scaled by 33, giving `2/3 = x/78` - arithmetic a
 * student does by long multiplication rather than by seeing the relationship.
 */
function multiplierFor(max: number, largest: number): number {
    return getRandomInt(2, Math.max(2, Math.min(12, Math.floor(max / largest))));
}

/**
 * A ratio to simplify. Built from the answer and a multiplier, so the greatest
 * common divisor is known before the question exists and is never 1 (ADR §3) -
 * `2 : 3` has nothing to do, and a sheet of those is a sheet of no questions.
 */
function buildSimplify(max: number): { prompt: string; answer: string } {
    const [first, second] = reducedPair(9);
    const largest = Math.max(first, second);
    const multiplier = multiplierFor(max, largest);
    return {
        prompt: `${first * multiplier}${COLON}${second * multiplier} = ______${COLON}______`,
        answer: `${first}${COLON}${second}`,
    };
}

/** `2 : 3 = 8 : ___`, with one side scaled up by a whole factor. */
function buildEquivalent(max: number): { prompt: string; answer: string } {
    const [first, second] = reducedPair(9);
    const largest = Math.max(first, second);
    const multiplier = multiplierFor(max, largest);
    return {
        prompt: `${first}${COLON}${second} = ${first * multiplier}${COLON}______`,
        answer: String(second * multiplier),
    };
}

/**
 * The same mathematics as Equivalent, in fraction notation (ADR §2).
 *
 * Both ship on purpose: a student who can do `2 : 3 = 8 : ___` and not
 * `3/4 = x/12` has learnt a notation rather than an idea.
 */
function buildProportion(max: number): { prompt: string; answer: string } {
    const drawn = reducedPair(9);
    // Never a denominator of one. `8/1 = x/7` is correct and reads as a
    // mistake - nobody writes a fraction over one - so a pair like 8 : 1 is
    // turned round rather than thrown away. A numerator of one is fine, and
    // `1/4 = x/12` is a good question.
    const [first, second] = drawn[1] === 1 ? [drawn[1], drawn[0]] : drawn;
    const largest = Math.max(first, second);
    const multiplier = multiplierFor(max, largest);
    return {
        prompt: `${first}/${second} = x/${second * multiplier},   x = ______`,
        answer: String(first * multiplier),
    };
}

/**
 * A rate, built from the answer so the division is exact.
 *
 * `120 miles in 3 hours` works; `120 miles in 7 hours` does not, and is never
 * drawn rather than rejected - the same construction ADR 0032 §4 uses for unit
 * conversion and ADR 0034 §3 for percent.
 */
function buildUnitRate(max: number, language: PrintedLanguage): { prompt: string; answer: string } {
    const pairs = RATE_PAIR_TABLE[language];
    const pair = pairs[getRandomInt(0, pairs.length - 1)];
    const rate = getRandomInt(2, Math.max(2, Math.floor(max / 2)));
    const units = getRandomInt(2, 12);
    return {
        prompt: PROMPTS[language].unitRate(rate * units, pair.of, units, pair.perPlural, pair.per),
        answer: String(rate),
    };
}

/**
 * What one Proportional Tables problem asks (ADR 0104 section 2). The
 * first question is dealt as two kinds, `yes` and `no`, so a page can balance
 * them.
 */
export type ProportionalKind = 'yes' | 'no' | 'constant' | 'fill';

const PROPORTIONAL_KINDS: ProportionalKind[] = ['yes', 'no', 'constant', 'fill'];

/** ADR 0104 section 3: four columns, five to fill in. */
const TABLE_COLUMNS = 4;
const FILL_COLUMNS = 5;
const LARGEST_X = 12;
const SMALLEST_CONSTANT = 2;
const LARGEST_CONSTANT = 12;
/** How far a table that is not proportional may sit above or below the origin. */
const LARGEST_INTERCEPT = 12;

/** `count` distinct whole numbers from 1 to `top`, in increasing order. */
function distinctXs(count: number, top: number): number[] {
    const pool = Array.from({ length: top }, (_, index) => index + 1);
    return shuffle(pool)
        .slice(0, count)
        .sort((a, b) => a - b);
}

/**
 * A proportional table: k first, then x values that keep k times the largest x
 * under the dial, so a small dial narrows x rather than failing (ADR 0104
 * section 3).
 */
function proportionalTable(columns: number, max: number): { k: number; xs: number[]; ys: number[] } {
    const k = getRandomInt(SMALLEST_CONSTANT, Math.min(LARGEST_CONSTANT, Math.floor(max / columns)));
    const xs = distinctXs(columns, Math.min(LARGEST_X, Math.floor(max / k)));
    return { k, xs, ys: xs.map((x) => k * x) };
}

/**
 * y = mx + b with b not zero, either sign, every y positive and none over the
 * dial. The y row rises by the same step at each equal step in x, so it looks
 * orderly; y / x changes, which is what 7.RP.A.2a is about.
 */
function notProportionalTable(max: number): { xs: number[]; ys: number[] } {
    const slope = getRandomInt(SMALLEST_CONSTANT, Math.min(LARGEST_CONSTANT, Math.floor((max - 1) / TABLE_COLUMNS)));
    const xs = distinctXs(TABLE_COLUMNS, Math.min(LARGEST_X, Math.floor((max - 1) / slope)));
    // Above the origin if there is room under the dial; below it keeps the
    // smallest y positive. Either way there is at least one step to take.
    const room = Math.min(LARGEST_INTERCEPT, max - slope * xs[xs.length - 1]);
    const depth = Math.min(LARGEST_INTERCEPT, slope * xs[0] - 1);
    const intercept = getRandomInt(0, 1) === 0 ? getRandomInt(1, room) : -getRandomInt(1, depth);
    return { xs, ys: xs.map((x) => slope * x + intercept) };
}

function buildProportional(
    kind: ProportionalKind,
    max: number,
    language: PrintedLanguage
): { prompt: string; answer: string; table: { x: (number | null)[]; y: (number | null)[] } } {
    const t = PROMPTS[language];

    if (kind === 'no') {
        const { xs, ys } = notProportionalTable(max);
        return { prompt: t.proportionalYesNo(), answer: t.proportionalNo(), table: { x: xs, y: ys } };
    }
    if (kind === 'fill') {
        const { xs, ys } = proportionalTable(FILL_COLUMNS, max);
        // One missing y (multiply by k) and one missing x (divide by k), never in
        // the same column, so three columns are left to find k from.
        const blankY = getRandomInt(0, FILL_COLUMNS - 1);
        const drawn = getRandomInt(0, FILL_COLUMNS - 2);
        const blankX = drawn >= blankY ? drawn + 1 : drawn;
        return {
            prompt: t.proportionalFill(),
            answer: `${ys[blankY]}, ${xs[blankX]}`,
            table: {
                x: xs.map((x, column) => (column === blankX ? null : x)),
                y: ys.map((y, column) => (column === blankY ? null : y)),
            },
        };
    }
    const { k, xs, ys } = proportionalTable(TABLE_COLUMNS, max);
    return kind === 'yes'
        ? { prompt: t.proportionalYesNo(), answer: t.proportionalYes(k), table: { x: xs, y: ys } }
        : { prompt: t.proportionalConstant(), answer: `k = ${k},  y = ${k}x`, table: { x: xs, y: ys } };
}

/**
 * What one Proportional Graphs problem asks (ADR 0105 section 2): the first
 * question dealt as `yes` and `no`, the constant and its equation, and a value
 * read past the edge of the grid.
 */
export type ProportionalGraphKind = 'yes' | 'no' | 'constant' | 'beyond';

const PROPORTIONAL_GRAPH_KINDS: ProportionalGraphKind[] = ['yes', 'no', 'constant', 'beyond'];

/**
 * The grid (ADR 0105 section 3): x from 0 to 10, one unit a gridline, and ten
 * gridlines up the y axis, each worth one of the steps a textbook counts in. The
 * axis tops out at ten steps, which the dial must allow.
 */
export const GRAPH_GRIDLINES = 10;
export const GRAPH_STEPS = [1, 2, 4, 5, 10];
const SMALLEST_GRAPH_CONSTANT = 2;
/** A line that leaves the grid sooner than this is a spike, not something to read. */
const SHORTEST_RUN = 3;
/** How far from the origin a line that is not proportional crosses an axis, in gridlines, at least and at most. */
export const NEAREST_CROSSING = 2;
const FARTHEST_CROSSING = 8;
/** Dots on a graph: two lattice crossings are enough to read a line from. */
export const GRAPH_DOTS = 2;
/** The x a "past the edge" question asks about: off the drawn grid (ADR 0105 section 2). */
const BEYOND_FROM = 11;
const BEYOND_TO = 30;

/**
 * The points of y = slope x + intercept that sit on gridline crossings inside
 * the grid, other than the origin: whole x from 1 to 10, y above the x axis and
 * no higher than the top, y a multiple of the scale. These are the points a
 * graph may dot.
 */
export function graphCrossings(slope: number, intercept: number, yStep: number): { x: number; y: number }[] {
    const crossings: { x: number; y: number }[] = [];
    for (let x = 1; x <= GRAPH_GRIDLINES; x++) {
        const y = slope * x + intercept;
        if (y > 0 && y <= GRAPH_GRIDLINES * yStep && y % yStep === 0) {
            crossings.push({ x, y });
        }
    }
    return crossings;
}

/**
 * Every (constant, scale) pair a proportional graph may use, found by the rule
 * and not from a list (ADR 0105 section 3): the line leaves the top of the grid
 * between x = 3 and x = 10, and at least two of its points land on crossings.
 * That allows k = 2, 3, 4, 5, 6, 8, 10, 12, 15, 20, 25 and 30; 7, 9 and 11 have
 * no clean scale among the five.
 */
export const PROPORTIONAL_GRAPH_PAIRS: { k: number; yStep: number }[] = GRAPH_STEPS.flatMap((yStep) =>
    Array.from({ length: GRAPH_GRIDLINES * yStep - SMALLEST_GRAPH_CONSTANT + 1 }, (_, i) => SMALLEST_GRAPH_CONSTANT + i)
        .filter((k) => {
            const exit = (GRAPH_GRIDLINES * yStep) / k;
            return exit >= SHORTEST_RUN && exit <= GRAPH_GRIDLINES && graphCrossings(k, 0, yStep).length >= GRAPH_DOTS;
        })
        .map((k) => ({ k, yStep }))
);

/**
 * Every line y = mx + b with b not zero that a "no" graph may use on a scale,
 * under the same rules as the "yes" ones (ADR 0105 section 3). m is a whole
 * number of at least 2, like the tables' slopes (ADR 0104). The line crosses an
 * axis at least `NEAREST_CROSSING` gridlines from the origin, so a student can
 * tell by eye that it misses: above on the y axis at b = j steps, or right of
 * the origin on the x axis at a whole x (b = -m x). It stays on the grid for at
 * least `SHORTEST_RUN` units of x, and has at least two crossings to dot. The
 * x-intercept itself is not dotted - dots are y above the axis - but it is
 * where the line enters the grid.
 */
export function notProportionalLines(yStep: number): { slope: number; intercept: number }[] {
    const top = GRAPH_GRIDLINES * yStep;
    const lines: { slope: number; intercept: number }[] = [];
    for (let slope = 2; slope <= top; slope++) {
        for (let far = NEAREST_CROSSING; far <= FARTHEST_CROSSING; far++) {
            // Above the origin: b = far steps up.
            const above = far * yStep;
            if (above < top && Math.min(GRAPH_GRIDLINES, (top - above) / slope) >= SHORTEST_RUN) {
                lines.push({ slope, intercept: above });
            }
            // Right of the origin: the line leaves the x axis at x = far.
            if (Math.min(GRAPH_GRIDLINES, far + top / slope) - far >= SHORTEST_RUN) {
                lines.push({ slope, intercept: -slope * far });
            }
        }
    }
    return lines.filter((line) => graphCrossings(line.slope, line.intercept, yStep).length >= GRAPH_DOTS);
}

const NOT_PROPORTIONAL_LINES: Record<number, { slope: number; intercept: number }[]> = Object.fromEntries(
    GRAPH_STEPS.map((yStep) => [yStep, notProportionalLines(yStep)])
);

const pick = <T,>(items: T[]): T => items[getRandomInt(0, items.length - 1)];

/** `GRAPH_DOTS` of a line's crossings, chosen at random, left to right. */
function dotsFor(slope: number, intercept: number, yStep: number): { x: number; y: number }[] {
    return shuffle(graphCrossings(slope, intercept, yStep))
        .slice(0, GRAPH_DOTS)
        .sort((a, b) => a.x - b.x);
}

/**
 * A proportional graph: k first, from the constants some scale can show, then a
 * scale that shows it and fits under the dial. The smallest dial (12) allows the
 * one-a-gridline scale, which shows k = 2 and 3.
 */
function proportionalGraph(max: number): { k: number; graph: NonNullable<ProblemData['ratioGraph']> } {
    const allowed = PROPORTIONAL_GRAPH_PAIRS.filter(({ yStep }) => GRAPH_GRIDLINES * yStep <= max);
    const k = pick([...new Set(allowed.map((pair) => pair.k))]);
    const { yStep } = pick(allowed.filter((pair) => pair.k === k));
    return { k, graph: { yStep, slope: k, intercept: 0, dots: dotsFor(k, 0, yStep) } };
}

/** A graph that is a straight line but misses the origin. */
function notProportionalGraph(max: number): NonNullable<ProblemData['ratioGraph']> {
    const yStep = pick(GRAPH_STEPS.filter((step) => GRAPH_GRIDLINES * step <= max && NOT_PROPORTIONAL_LINES[step].length > 0));
    const { slope, intercept } = pick(NOT_PROPORTIONAL_LINES[yStep]);
    return { yStep, slope, intercept, dots: dotsFor(slope, intercept, yStep) };
}

function buildProportionalGraph(
    kind: ProportionalGraphKind,
    max: number,
    language: PrintedLanguage
): { prompt: string; answer: string; graph: NonNullable<ProblemData['ratioGraph']> } {
    const t = PROMPTS[language];
    if (kind === 'no') {
        return { prompt: t.proportionalYesNo(), answer: t.proportionalNo(), graph: notProportionalGraph(max) };
    }
    const { k, graph } = proportionalGraph(max);
    if (kind === 'yes') {
        return { prompt: t.proportionalYesNo(), answer: t.proportionalYes(k), graph };
    }
    if (kind === 'constant') {
        return { prompt: t.proportionalConstant(), answer: `k = ${k},  y = ${k}x`, graph };
    }
    const x = getRandomInt(BEYOND_FROM, BEYOND_TO);
    return { prompt: t.proportionalBeyond(x), answer: String(k * x), graph };
}

/**
 * One ratio problem. Always succeeds - every arm is built from its answer.
 *
 * `kind` only matters to Proportional Tables, which a page deals itself
 * (`generateRatioPages`); asked for without one, it draws any of the four.
 */
export function buildRatioProblem(
    question: RatioQuestionOptions,
    maxValue: number,
    key: number,
    language: PrintedLanguage = 'en',
    kind?: ProportionalKind | ProportionalGraphKind
): ProblemData {
    const max = usableMax(maxValue);

    if (question === RatioQuestionOptions.ProportionalGraphs) {
        const asked = (kind as ProportionalGraphKind | undefined) ?? PROPORTIONAL_GRAPH_KINDS[getRandomInt(0, 3)];
        const built = buildProportionalGraph(asked, max, language);
        return {
            key,
            numbers: [],
            operator: "",
            display: [built.prompt, built.answer],
            ratioGraph: built.graph,
            ratioQuestion: question,
        };
    }

    if (question === RatioQuestionOptions.Proportional) {
        const built = buildProportional((kind as ProportionalKind | undefined) ?? PROPORTIONAL_KINDS[getRandomInt(0, 3)], max, language);
        return {
            key,
            numbers: [],
            operator: "",
            display: [built.prompt, built.answer],
            ratioTable: built.table,
            ratioQuestion: question,
        };
    }

    let built: { prompt: string; answer: string };

    switch (question) {
        case RatioQuestionOptions.Equivalent:
            built = buildEquivalent(max);
            break;
        case RatioQuestionOptions.UnitRate:
            built = buildUnitRate(max, language);
            break;
        case RatioQuestionOptions.Proportion:
            built = buildProportion(max);
            break;
        default:
            built = buildSimplify(max);
            break;
    }

    return {
        key,
        numbers: [],
        operator: "",
        display: [built.prompt, built.answer],
        ratioQuestion: question,
    };
}

/**
 * The kinds one printed page deals: the three questions as evenly as the count
 * allows, with the yes and no tables splitting the first's share as evenly as it
 * allows. Which question, and which of yes and no, gets an odd one out is
 * random. Exactly `count` kinds, so the page shuffles each one once.
 */
function proportionalPlan<Third extends string>(count: number, third: Third): ('yes' | 'no' | 'constant' | Third)[] {
    const dealt = questionSequence<'yesNo' | 'constant' | Third>(['yesNo', 'constant', third], count);
    const yesNo = dealt.filter((question) => question === 'yesNo').length;
    const yes = Math.floor(yesNo / 2) + (yesNo % 2 === 1 ? getRandomInt(0, 1) : 0);
    let seen = 0;
    return dealt.map((question): 'yes' | 'no' | 'constant' | Third => {
        if (question !== 'yesNo') {
            return question as 'constant' | Third;
        }
        return seen++ < yes ? 'yes' : 'no';
    });
}

// A table's or graph's prompt repeats from problem to problem; the drawing is
// what makes two of them the same.
function fingerprint(problem: ProblemData): string {
    const table = problem.ratioTable;
    if (table) {
        return `${problem.display![0]}|${table.x.join(',')}|${table.y.join(',')}`;
    }
    const graph = problem.ratioGraph;
    return graph
        ? `${problem.display![0]}|${graph.yStep}|${graph.slope}|${graph.intercept}`
        : problem.display![0];
}

/** A page-shaped set of ratio problems. */
export function generateRatioPages(settings: ISettingsProps): ProblemData[][] | null {
    const drawn = settings.RatioQuestion;
    if (drawn === RatioQuestionOptions.Proportional || drawn === RatioQuestionOptions.ProportionalGraphs) {
        // Dealt page by page, not across the sheet: each printed page, and each
        // half of a half page (one call here per half), goes to a student and
        // must hold the three questions evenly (ADR 0104 section 2, 0105
        // section 2).
        const maxValue = settings.RatioMaxValue ?? 50;
        const language = printedLanguageOf(settings);
        const perPage = settings.NumberOfProblemsPerPage;
        const graphs = drawn === RatioQuestionOptions.ProportionalGraphs;
        const pages: ProblemData[][] = [];
        for (let page = 0; page < settings.NumberOfPages; page++) {
            const onePage = generateProblemPages({
                settings: { ...settings, NumberOfPages: 1 },
                choices: graphs ? proportionalPlan(perPage, 'beyond') : proportionalPlan(perPage, 'fill'),
                build: (kind: ProportionalKind | ProportionalGraphKind, key) =>
                    buildRatioProblem(drawn, maxValue, key, language, kind),
                fingerprint,
            });
            if (onePage === null) {
                return pages.length === 0 ? null : pages;
            }
            // Keys number the whole sheet, not the page they were drawn on.
            pages.push(onePage[0].map((problem) => ({ ...problem, key: page * perPage + problem.key })));
        }
        return pages;
    }
    const types = questionTypes(settings.RatioQuestion ?? RatioQuestionOptions.Mixed);
    const maxValue = settings.RatioMaxValue ?? 50;
    const language = printedLanguageOf(settings);
    return generateProblemPages({
        settings,
        choices: types,
        build: (question, key) => buildRatioProblem(question, maxValue, key, language),
        fingerprint,
    });
}
